import { LineChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { useEffect, useRef } from "react";
import { useI18n } from "../i18n";
import type { CompareAlignBy } from "../shared/contracts";

echarts.use([
  LineChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

interface ChartSeries {
  key: string;
  label: string;
  color: string;
  axis?: 0 | 1;
  area?: boolean;
}

export function MetricTrendChart<T extends object>({
  points,
  comparePoints,
  compareLabel = "",
  compareAlignBy = "date_index",
  xKey,
  series,
  percentAxis = false,
}: {
  points: T[];
  comparePoints?: T[];
  compareLabel?: string;
  compareAlignBy?: CompareAlignBy;
  xKey: string;
  series: ChartSeries[];
  percentAxis?: boolean;
}) {
  const { locale, text } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const resolvedCompareLabel = compareLabel || text.dashboard.noComparison;

  useEffect(() => {
    if (!ref.current) return;
    const chart = echarts.init(ref.current);
    const hasSecondAxis = series.some((item) => item.axis === 1);
    const primarySeries = series.map((item) => ({
      name: item.label,
      type: "line",
      yAxisIndex: item.axis || 0,
      smooth: true,
      showSymbol: points.length < 15,
      symbolSize: 5,
      data: points.map(
        (point) =>
          (point as Record<string, string | number | null>)[item.key],
      ),
      lineStyle: { width: 2, color: item.color },
      itemStyle: { color: item.color },
      areaStyle: item.area ? { color: `${item.color}18` } : undefined,
    }));
    const comparisonSeries = comparePoints?.length
      ? series.map((item) => ({
          name: `${item.label} (${resolvedCompareLabel})`,
          type: "line",
          yAxisIndex: item.axis || 0,
          smooth: true,
          showSymbol: false,
          data: mapCompareData(points, comparePoints, xKey, item.key, compareAlignBy),
          lineStyle: {
            width: 1.6,
            color: item.color,
            opacity: 0.55,
            type: "dashed",
          },
          itemStyle: { color: item.color, opacity: 0.55 },
        }))
      : [];

    chart.setOption({
      animationDuration: 400,
      tooltip: {
        trigger: "axis",
        backgroundColor: "#182026",
        borderWidth: 0,
        textStyle: { color: "#fff", fontSize: 11 },
      },
      legend: {
        top: 0,
        right: 0,
        itemWidth: 9,
        itemHeight: 9,
        textStyle: { color: "#64717a", fontSize: 10 },
      },
      grid: {
        top: 42,
        right: hasSecondAxis ? 48 : 14,
        bottom: 28,
        left: 48,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: points.map((point) =>
          formatDate(
            String(
              (point as Record<string, string | number | null>)[xKey] || "",
            ),
            locale,
          ),
        ),
        axisLine: { lineStyle: { color: "#d9e0e4" } },
        axisTick: { show: false },
        axisLabel: { color: "#78858d", fontSize: 9 },
      },
      yAxis: [
        {
          type: "value",
          min: 0,
          axisLabel: {
            color: "#78858d",
            fontSize: 9,
            formatter: percentAxis ? "{value}%" : "{value}",
          },
          splitLine: { lineStyle: { color: "#edf0f2" } },
        },
        ...(hasSecondAxis
          ? [
              {
                type: "value",
                min: 0,
                axisLabel: { color: "#78858d", fontSize: 9 },
                splitLine: { show: false },
              },
            ]
          : []),
      ],
      series: [...primarySeries, ...comparisonSeries],
    });

    const resize = () => chart.resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      chart.dispose();
    };
  }, [
    compareAlignBy,
    comparePoints,
    locale,
    percentAxis,
    points,
    resolvedCompareLabel,
    series,
    xKey,
  ]);

  return <div className="chart-canvas" ref={ref} />;
}

function mapCompareData<T extends object>(
  points: T[],
  comparePoints: T[],
  xKey: string,
  valueKey: string,
  alignBy: CompareAlignBy,
) {
  const read = (point: T, key: string) =>
    (point as Record<string, string | number | null>)[key];
  if (alignBy === "calendar_date") {
    const byDate = new Map(comparePoints.map((point) => [read(point, xKey), point]));
    return points.map((point) => {
      const compare = byDate.get(read(point, xKey));
      return compare ? read(compare, valueKey) : null;
    });
  }
  return points.map((_, index) =>
    comparePoints[index] ? read(comparePoints[index], valueKey) : null,
  );
}

function formatDate(value: string, locale: string) {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
      }).format(date);
}
